import type { UserResponse } from "@/client"

/**
 * Gets a display name for a user from their available profile information.
 *
 * @param user - The user object from the API response or internal state
 * @param fallback - The fallback string to use if no name information is available
 * @returns A display name (full name, given/family name, email prefix, or fallback)
 */
export const getUserDisplayName = (
  user: Partial<Pick<UserResponse, "name" | "given_name" | "family_name" | "email">>,
  fallback: string = "User",
): string => {
  const fullName = user.name?.trim()
  if (fullName) {
    return fullName
  }

  const given = user.given_name?.trim() ?? ""
  const family = user.family_name?.trim() ?? ""
  const fallbackName = `${given} ${family}`.trim()

  if (fallbackName) {
    return fallbackName
  }

  if (user.email) {
    const emailName = user.email.split("@")[0]
    if (emailName) {
      return emailName
    }
  }

  return fallback
}

/**
 * Gets initials (up to two characters) from a display name for avatar fallbacks.
 *
 * @param name - Display name to derive initials from.
 * @returns Uppercase initials, or "?" when the name is blank.
 */
export const getInitials = (name: string): string => {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter((part) => part.length > 0)
  if (parts.length === 0) {
    return "?"
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase()
  }
  return `${parts[0][0]}${parts.at(-1)?.[0] ?? ""}`.toUpperCase()
}
